import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Brain, Sparkles, Zap, Lock, Loader2, X, ArrowRight, Crown } from "lucide-react";
import { useLanguage } from "@/i18n";
import { useMemo } from "react";

/**
 * Mini upgrade modal triggered when a free/anonymous user taps the locked "Pro" AI mode card.
 * One-click checkout (Stripe) for the recommended plan + a link to view all plans.
 */
export const UpgradeModal = ({
  open,
  onOpenChange,
  subscriptionPlans = [],
  onSubscribe,
  onViewAllPlans,
  isProcessingPayment = false,
  user = null,
  onRequireLogin
}) => {
  const { t } = useLanguage();

  // Pick recommended plan: prefer "pro" tier, fallback to first plan
  const recommendedPlan = useMemo(() => {
    if (!subscriptionPlans?.length) return null;
    return (
      subscriptionPlans.find((p) => /pro/i.test(p.id) || /pro/i.test(p.name || "")) ||
      subscriptionPlans[0]
    );
  }, [subscriptionPlans]);

  const handleUpgradeNow = () => {
    if (!user) {
      onRequireLogin?.();
      return;
    }
    if (recommendedPlan) {
      onSubscribe?.(recommendedPlan.id);
    } else {
      onViewAllPlans?.();
    }
  };

  const handleViewAll = () => {
    onViewAllPlans?.();
  };

  const planLabel = recommendedPlan
    ? (recommendedPlan.name_key && t(recommendedPlan.name_key) !== recommendedPlan.name_key
        ? t(recommendedPlan.name_key)
        : recommendedPlan.name)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="upgrade-modal"
        className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/40 border-purple-500/30 max-w-md p-0 overflow-hidden"
      >
        {/* Glow accent */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-500/10 to-transparent pointer-events-none" />
          <button
            onClick={() => onOpenChange(false)}
            data-testid="upgrade-modal-close"
            className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300 transition-colors"
            aria-label="close"
          >
            <X size={16} />
          </button>

          <div className="p-6 pb-5 relative">
            {/* Header icon */}
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/40">
                  <Brain size={32} className="text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-500 border-2 border-zinc-900 flex items-center justify-center">
                  <Lock size={12} className="text-zinc-900" />
                </div>
              </div>
            </div>

            <h2
              className="text-xl font-bold text-white text-center mb-1"
              data-testid="upgrade-modal-title"
            >
              {t("upgrade_pro_title")}
            </h2>
            <p className="text-sm text-zinc-400 text-center mb-5">
              {t("upgrade_pro_subtitle")}
            </p>

            {/* Benefits */}
            <div className="space-y-2.5 mb-5">
              <BenefitRow icon={Brain} color="text-purple-400" text={t("upgrade_benefit_1")} />
              <BenefitRow icon={Sparkles} color="text-pink-400" text={t("upgrade_benefit_2")} />
              <BenefitRow icon={Zap} color="text-amber-400" text={t("upgrade_benefit_3")} />
            </div>

            {/* Recommended plan card */}
            {recommendedPlan && (
              <div
                className="rounded-xl border border-purple-500/40 bg-purple-500/10 p-4 mb-4"
                data-testid="upgrade-recommended-plan"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Crown size={14} className="text-amber-400" />
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                      {t("recommended")}
                    </span>
                  </div>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-base font-semibold text-white">{planLabel}</span>
                  <span className="text-2xl font-bold text-purple-300">
                    ₺{recommendedPlan.price}
                    <span className="text-xs text-zinc-400 font-normal">
                      /{t("per_month_short")}
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* CTAs */}
            <Button
              onClick={handleUpgradeNow}
              disabled={isProcessingPayment}
              data-testid="upgrade-now-btn"
              className="w-full h-11 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold shadow-lg shadow-purple-500/30 disabled:opacity-60"
            >
              {isProcessingPayment ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  {t("upgrade_now")}
                  <ArrowRight size={16} className="ml-1" />
                </>
              )}
            </Button>

            <button
              onClick={handleViewAll}
              data-testid="upgrade-view-all-plans-btn"
              className="w-full mt-2 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
            >
              {t("view_all_plans")}
            </button>

            <button
              onClick={() => onOpenChange(false)}
              data-testid="upgrade-maybe-later-btn"
              className="w-full mt-1 py-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              {t("maybe_later")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BenefitRow = ({ icon: Icon, color, text }) => (
  <div className="flex items-start gap-3 text-sm">
    <div className="w-6 h-6 rounded-md bg-zinc-800/70 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon size={14} className={color} />
    </div>
    <span className="text-zinc-200 leading-snug">{text}</span>
  </div>
);

export default UpgradeModal;
